import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarDays, Clock, MapPin, ScanLine, Ticket } from "lucide-react";
import {
  hidratarEventosRecepcion,
  isRecepcionAutenticado,
} from "@/data/recepcionStore";
import { clearReceptionSessionLocal } from "@/data/receptionAuthStore";
import EmptyState from "@/components/common/EmptyState";
import EventosCargaError from "@/screens/reception/EventosCargaError";
import { RECEPCION } from "@/constants/testIds";

export default function SeleccionEvento() {
  const navigate = useNavigate();
  const [carga, setCarga] = useState({ estado: "cargando" });

  const cargar = useCallback(async () => {
    setCarga({ estado: "cargando" });
    const r = await hidratarEventosRecepcion();
    setCarga(r.ok ? { estado: "ok", eventos: r.eventos } : { estado: "error", error: r.error });
  }, []);

  useEffect(() => {
    if (!isRecepcionAutenticado()) {
      navigate("/recepcion/login", { replace: true });
      return;
    }
    cargar();
  }, [navigate, cargar]);

  const relogin = () => {
    clearReceptionSessionLocal();
    navigate("/recepcion/login", { replace: true });
  };

  const eventos = carga.estado === "ok" ? carga.eventos : [];

  return (
    <div
      data-testid={RECEPCION.seleccionScreen}
      className="flex flex-col gap-8"
    >
      <header className="animate-fade-up">
        <span className="overline text-accent">Portal de recepción</span>
        <h1 className="mt-3 font-heading text-3xl sm:text-4xl tracking-tight text-foreground">
          Selecciona un evento
        </h1>
        <p className="mt-3 text-muted-foreground max-w-xl leading-relaxed">
          Escoge el evento para el que vas a validar accesos. Todas las
          lecturas de QR se aplicarán únicamente sobre el evento seleccionado.
        </p>
      </header>

      {carga.estado === "cargando" ? (
        <p
          data-testid={RECEPCION.seleccionLoading}
          className="text-muted-foreground animate-pulse"
        >
          Cargando eventos…
        </p>
      ) : carga.estado === "error" ? (
        <EventosCargaError error={carga.error} onReintentar={cargar} onRelogin={relogin} />
      ) : eventos.length === 0 ? (
        <div className="animate-fade-up delay-100">
          <EmptyState
            icon={Ticket}
            eyebrow="Sin eventos"
            title="No hay eventos con boletos emitidos."
            description="Cuando se emitan boletos digitales, los eventos disponibles aparecerán aquí para su validación."
            testId={RECEPCION.seleccionEmpty}
          />
        </div>
      ) : (
        <section
          data-testid={RECEPCION.seleccionList}
          className="grid grid-cols-1 md:grid-cols-2 gap-5 animate-fade-up delay-100"
        >
          {eventos.map((ev) => (
            <button
              key={ev.eventoId}
              type="button"
              data-testid={RECEPCION.seleccionItem(ev.eventoId)}
              onClick={() => navigate(`/recepcion/eventos/${ev.eventoId}`)}
              className="group text-left rounded-2xl border hairline bg-card p-6 md:p-7 shadow-soft hover:shadow-md hover:-translate-y-0.5 transition-all"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <span className="overline text-accent">Evento</span>
                  <h3 className="mt-2 font-heading text-xl sm:text-2xl text-foreground leading-snug">
                    {ev.nombreEvento || "—"}
                  </h3>
                </div>
                <div className="shrink-0 w-11 h-11 grid place-items-center rounded-xl bg-primary/10 dark:bg-accent/15 text-primary dark:text-accent">
                  <ScanLine className="w-5 h-5" strokeWidth={1.6} />
                </div>
              </div>

              <ul className="mt-5 flex flex-col gap-2 text-sm text-muted-foreground">
                {ev.fechaEvento && (
                  <li className="flex items-center gap-2">
                    <CalendarDays
                      className="w-4 h-4 text-accent"
                      strokeWidth={1.6}
                    />
                    {ev.fechaEvento}
                  </li>
                )}
                {ev.horaEvento && (
                  <li className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-accent" strokeWidth={1.6} />
                    {ev.horaEvento}
                  </li>
                )}
                {ev.salon && (
                  <li className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-accent" strokeWidth={1.6} />
                    {ev.salon}
                  </li>
                )}
              </ul>

              <div className="mt-6 pt-5 border-t hairline grid grid-cols-3 gap-3 text-xs">
                <div>
                  <div className="overline text-muted-foreground">Total</div>
                  <div className="mt-1 font-heading text-lg text-foreground">
                    {ev.total}
                  </div>
                </div>
                <div>
                  <div className="overline text-muted-foreground">Listos</div>
                  <div className="mt-1 font-heading text-lg text-foreground">
                    {ev.listos}
                  </div>
                </div>
                <div>
                  <div className="overline text-muted-foreground">
                    Validados
                  </div>
                  <div className="mt-1 font-heading text-lg text-accent">
                    {ev.validados}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </section>
      )}
    </div>
  );
}
